import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Stack,
  Text,
} from "@mantine/core";
import { Link, Route, Routes, useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ErrorView, PageTitle, Subnav, Can } from "@components/shared";
import { ConceptualRootItems } from "@components/ConceptualRoot";
import ConceptualItemDetail from "./ConceptualItemDetail";
import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";
import { PERMISSIONS } from "@util/permissions";

function statusColor(status) {
  if (status === 1) return "green";
  if (status === 2) return "gray";
  if (status === 3) return "red";
  return "gray";
}

export default function ConceptualRootDetail() {
  const { t } = useTranslation();
  const [error, setError] = useState(null);
  const [root, setRoot] = useState(null);
  const [toggling, setToggling] = useState(false);
  const params = useParams();
  const location = useLocation();
  const rootId = params.id;
  const isItemDetail = /\/items\/[^/]+\/?$/.test(location.pathname);

  useEffect(() => {
    fetchData();
  }, [rootId]);

  function fetchData() {
    axios
      .get(`/conceptual-roots/${rootId}/`)
      .then(({ data }) => {
        setRoot(data.response[0]);
        setError(null);
      })
      .catch((err) => {
        setError(err);
        setRoot(null);
      });
  }

  function toggleActive() {
    setToggling(true);
    axios
      .post(`/conceptual-roots/${rootId}/active-toggle/`)
      .then(() => {
        setToggling(false);
        toast.success(t("updated"));
        fetchData();
      })
      .catch((err) => {
        setToggling(false);
        toast.error(err);
      });
  }

  if (error) {
    return <ErrorView text={error} />;
  }

  if (!root) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      {isItemDetail ? (
        <Text size="sm" c="dimmed" mb="md">
          <Anchor
            component={Link}
            to={`/conceptual-roots/${rootId}/items`}
            size="sm"
            c="dimmed"
            underline="hover"
          >
            {root.label || root.concept}
          </Anchor>
          <Text span> · Conceptual Root</Text>
          {root.status_formatted ? (
            <Text span> · {root.status_formatted}</Text>
          ) : null}
        </Text>
      ) : (
        <>
          <Group mb="lg">
            <PageTitle
              title={root.label || root.concept}
              subtitle="Conceptual Root"
              icon={entityIcon.question()}
              iconColor={entityColor.role}
            />
            <Badge variant="light" color={statusColor(root.status)}>
              {root.status_formatted || t("status")}
            </Badge>
          </Group>
          <Box mt="md" mb="md">
            <Subnav
              links={[
                { text: t("detail"), to: `/conceptual-roots/${rootId}` },
                {
                  text: t("item_plural"),
                  to: `/conceptual-roots/${rootId}/items`,
                },
              ]}
            />
          </Box>
        </>
      )}
      <Routes>
        <Route
          path="/"
          element={
            <Stack gap="sm">
              <Card>
                <Stack gap="xs">
                  <Text>
                    <Text span fw={700}>
                      Concept:{" "}
                    </Text>
                    {root.concept}
                  </Text>
                  <Text>
                    <Text span fw={700}>
                      Label:{" "}
                    </Text>
                    {root.label}
                  </Text>
                  <Text>
                    <Text span fw={700}>
                      {t("variety")}:{" "}
                    </Text>
                    {root.variety_formatted || root.variety}
                  </Text>
                  <Text>
                    <Text span fw={700}>
                      Composite:{" "}
                    </Text>
                    {root.is_composite ? "Yes" : "No"}
                  </Text>
                </Stack>
              </Card>
              <Can permission={PERMISSIONS.CONCEPTUAL_ROOTS_EDIT}>
                <Card>
                  <Button
                    variant="light"
                    loading={toggling}
                    onClick={toggleActive}
                  >
                    {t("activation_status")}
                  </Button>
                </Card>
              </Can>
            </Stack>
          }
        />
        <Route
          path="/items"
          element={
            <ConceptualRootItems
              rootId={rootId}
              variety={root.variety}
            />
          }
        />
        <Route path="/items/:itemId" element={<ConceptualItemDetail />} />
      </Routes>
    </div>
  );
}
